import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service.js';
import { decryptToken } from '../../utils/decryptToken.js';
import { getRefreshToken } from '../../utils/httpHelperFunctions.js';
import { catchError, map, of, switchMap } from 'rxjs';

function normalizePathForGuard(url: string): string {
  const path = (url.startsWith('/') ? url : `/${url}`).split(/[?#]/)[0];
  return path;
}

/** Login entry routes that clear storage on load — block when session is active. */
export function isLoginEntryPath(url: string): boolean {
  const path = normalizePathForGuard(url);
  return path === '/' || path === '/auth/login';
}

/** Auth/onboarding routes users should not return to via back while logged in. */
export function isAuthEntryPath(url: string): boolean {
  const path = normalizePathForGuard(url);
  return isLoginEntryPath(url) ||
    path.startsWith('/auth/login/') ||
    path.startsWith('/onboarding');
}

/**
 * Checks if the current session is valid
 * @returns true if session is valid, false otherwise
 */
export function hasValidSession(): boolean {
  const accessToken = localStorage.getItem('access_token');
  const refreshToken = getRefreshToken();

  // If no tokens exist, session is invalid
  if (!accessToken || !refreshToken) {
    return false;
  }

  // Check if access token is expired
  try {
    const decryptedToken = decryptToken(accessToken);
    const expiryTimeMs = Number(localStorage.getItem('sessionExpiryTime'));
    const currentTime = Math.floor(Date.now() / 1000);

    // If we have expiry time and token is expired, session is invalid
    if (expiryTimeMs && currentTime >= expiryTimeMs) {
      return false;
    }

    // Token exists and is not expired
    return true;
  } catch (error) {
    // If we can't decrypt the token, consider session invalid
    return false;
  }
}

function isProviderRole(role: string | undefined | null): boolean {
  if (!role) {
    return false;
  }

  const normalized = role.toUpperCase();
  return normalized === 'REA' || normalized === 'SERVICE_PROVIDER';
}

/** True when the current session belongs to a provider (REA / service provider). */
export function isProviderUser(): boolean {
  try {
    // Providers persist agentProfile; downsizer sessions persist userData instead.
    if (localStorage.getItem('agentProfile')) {
      return true;
    }

    if (localStorage.getItem('agentId') && !localStorage.getItem('userData')) {
      return true;
    }

    const accessToken = localStorage.getItem('access_token');
    if (accessToken) {
      const role = decryptToken(accessToken)?.role;
      if (isProviderRole(role)) {
        return true;
      }
    }

    return false;
  } catch {
    return false;
  }
}

/** Downsizer onboarding statuses that have not yet completed the signup wizard. */
const ONBOARDING_INCOMPLETE_STATUSES = ['STEP1', 'STEP2', 'STEP3'];

/** True when the current session belongs to a downsizer still mid-onboarding. */
export function isOnboardingIncomplete(): boolean {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return ONBOARDING_INCOMPLETE_STATUSES.includes(user?.status);
  } catch {
    return false;
  }
}

export function getAuthenticatedHomeUrl(): string {
  if (isProviderUser()) {
    return '/provider/dashboard';
  }

  return isOnboardingIncomplete() ? '/onboarding' : '/dashboard';
}

/**
 * Attempts to refresh the token
 * @param authService The auth service instance
 * @param refreshToken The refresh token to use
 * @returns Observable<boolean> - true if refresh succeeded, false otherwise
 */
function attemptTokenRefresh(authService: AuthService, refreshToken: string) {
  return authService.refreshToken(refreshToken).pipe(
    map((response: any) => {
      // Update tokens and expiry time
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
      const decryptedToken = decryptToken(response.access_token);
      localStorage.setItem('sessionExpiryTime', decryptedToken.exp);
      return true;
    }),
    catchError((error) => {
      // Refresh failed - clear tokens
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('sessionExpiryTime');
      return of(false);
    })
  );
}

export const authGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const path = normalizePathForGuard(state.url);

  // Allow access to root path without authentication
  if (path === '/' || path === '') {
    return true;
  }

  // Allow access to auth routes (login, signup, etc.) without authentication
  // These are child routes of the root path
  if (path.startsWith('/auth/')) {
    return true;
  }

  // Allow access to public routes without authentication (path-only; no session)
  if (path.startsWith('/shared-property/')) {
    return true;
  }

  if (path.startsWith('/shared-team-member/')) {
    return true;
  }

  // Check if session is valid
  if (hasValidSession()) {
    return true;
  }

  // Session is invalid - try to refresh if we have a refresh token
  const refreshToken = getRefreshToken();
  if (refreshToken) {
    return attemptTokenRefresh(authService, refreshToken).pipe(
      switchMap((refreshSuccess) => {
        if (refreshSuccess) {
          return of(true);
        } else {
          // Refresh failed - redirect to root
          router.navigate(['/']);
          return of(false);
        }
      })
    );
  }

  // No refresh token - redirect to root
  router.navigate(['/']);
  return false;
};

/** Keeps logged-in users off login screens (e.g. browser back into auth history). */
export const guestOnlyGuard: CanActivateFn = () => {
  if (!hasValidSession()) {
    return true;
  }

  return inject(Router).createUrlTree([getAuthenticatedHomeUrl()]);
};

/** Keeps providers, and downsizers who haven't finished onboarding, off the dashboard. */
export const downsizerDashboardGuard: CanActivateFn = () => {
  if (isProviderUser()) {
    return inject(Router).createUrlTree(['/provider/dashboard']);
  }

  if (isOnboardingIncomplete()) {
    return inject(Router).createUrlTree(['/onboarding']);
  }

  return true;
};

/** Guards the provider dashboard — requires an active provider session. */
export const providerAuthGuard: CanActivateFn = () => {
  if (hasValidSession() && isProviderUser()) {
    return true;
  }

  return inject(Router).createUrlTree(['/provider']);
};
